import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge } from "@vimos/ui";
import { supabase } from "../../lib/supabase";
import type { Lead } from "@vimos/api-client";

const STATUS_TONE: Record<Lead["status"], "neutral" | "market" | "signal"> = {
  nuevo: "signal",
  contactado: "neutral",
  en_conversacion: "neutral",
  cotizacion: "signal",
  ganado: "market",
  perdido: "neutral",
};

const statuses: Array<{ value: Lead["status"]; label: string }> = [
  { value: "nuevo", label: "Nuevo" },
  { value: "contactado", label: "Contactado" },
  { value: "en_conversacion", label: "En conversacion" },
  { value: "cotizacion", label: "Cotizacion" },
  { value: "ganado", label: "Ganado" },
  { value: "perdido", label: "Perdido" },
];

async function fetchLeads(): Promise<Lead[]> {
  const { data, error } = await supabase.from("leads").select("*").order("created_at", { ascending: false });
  if (error) throw error;
  return data ?? [];
}

export function LeadsPage() {
  const queryClient = useQueryClient();
  const { data: leads, isLoading, error } = useQuery({ queryKey: ["leads"], queryFn: fetchLeads });

  const updateStatus = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Lead["status"] }) => {
      const { error: updateError } = await supabase
        .from("leads")
        .update({ status } as never)
        .eq("id", id);
      if (updateError) throw updateError;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["leads"] }),
  });

  return (
    <div>
      <h1 className="font-display text-ink text-2xl font-bold">Leads</h1>
      <p className="text-mist mt-1 text-sm">Captura publica sin convertirse en CRM completo.</p>
      {isLoading && <p className="text-mist mt-6">Cargando...</p>}
      {error && <p className="mt-6 text-red-600">No pudimos cargar los leads.</p>}

      <div className="border-sand/40 bg-paper mt-6 overflow-x-auto rounded-lg border">
        <table className="w-full text-left text-sm">
          <thead className="border-sand/40 text-mist border-b text-xs uppercase">
            <tr>
              <th className="px-4 py-3">Nombre</th>
              <th className="px-4 py-3">Contacto</th>
              <th className="px-4 py-3">Servicio</th>
              <th className="px-4 py-3">Presupuesto</th>
              <th className="px-4 py-3">Mensaje</th>
              <th className="px-4 py-3">Origen</th>
              <th className="px-4 py-3">Estado</th>
            </tr>
          </thead>
          <tbody className="divide-sand/40 divide-y">
            {leads?.length === 0 ? (
              <tr>
                <td className="text-mist px-4 py-6" colSpan={7}>
                  Todavia no hay leads registrados.
                </td>
              </tr>
            ) : null}
            {leads?.map((lead) => (
              <tr key={lead.id}>
                <td className="px-4 py-3">
                  <p className="text-ink font-medium">{lead.name}</p>
                  {lead.company && <p className="text-mist text-xs">{lead.company}</p>}
                </td>
                <td className="text-ink/80 px-4 py-3">
                  {lead.email}
                  {lead.phone ? ` - ${lead.phone}` : ""}
                </td>
                <td className="text-mist px-4 py-3">{lead.service ?? "-"}</td>
                <td className="text-mist px-4 py-3">{lead.budget ?? "-"}</td>
                <td className="text-mist max-w-xs px-4 py-3">{lead.message ?? "-"}</td>
                <td className="text-mist px-4 py-3">{lead.source_page ?? "-"}</td>
                <td className="px-4 py-3">
                  <select
                    value={lead.status}
                    onChange={(event) =>
                      updateStatus.mutate({ id: lead.id, status: event.target.value as Lead["status"] })
                    }
                    className="border-sand/60 bg-paper rounded-md border px-2 py-1 text-sm"
                  >
                    {statuses.map((status) => (
                      <option key={status.value} value={status.value}>
                        {status.label}
                      </option>
                    ))}
                  </select>
                  <Badge tone={STATUS_TONE[lead.status]} className="ml-2">
                    {statuses.find((status) => status.value === lead.status)?.label}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
